import type { ReactNode } from "react";
import type { DfyOrderDetailsDto } from "@repo/api/dfy";
import type { AssetMediaDto } from "@repo/api/asset-media";
import { IconDownload } from "@tabler/icons-react";
import { cn, downloadFile } from "@/lib/utils";
import { DfyApi } from "@/lib/api/dfy-api";
import { Button } from "@/lib/components/Button/Button";
import Toast from "@/lib/components/Toast";
import { getDfyRequiredSections } from "@/lib/stores/dfy-order-store/dfy-order-selectors";

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="shrink-0">{label}</dt>
      <dd className="min-w-0 text-right wrap-anywhere whitespace-pre-wrap text-text-secondary">
        {value.trim() || "—"}
      </dd>
    </div>
  );
}

function BrandingGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 border-t pt-4 first:border-t-0 first:pt-0">
      <p className="font-medium">{title}</p>
      {children}
    </div>
  );
}

function BrandingImage({
  assetMedia,
  fit,
  onDownload,
}: {
  assetMedia: AssetMediaDto | undefined;
  fit: "contain" | "cover";
  onDownload: (assetMediaExternalId: string) => void;
}) {
  if (!assetMedia?.src) {
    return <p className="text-text-secondary">—</p>;
  }

  return (
    <div className="group relative w-full max-w-40">
      <img
        src={assetMedia.src}
        alt=""
        className={cn(
          "h-28 w-full rounded-lg border bg-white",
          fit === "contain" ? "object-contain p-1" : "object-cover",
        )}
      />
      <Button
        variant="ghost"
        size="iconSm"
        className="absolute right-2 bottom-2 bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/70 hover:text-white focus-visible:opacity-100"
        aria-label="Download image"
        onClick={() => onDownload(assetMedia.externalId)}
      >
        <IconDownload size={18} />
      </Button>
    </div>
  );
}

export default function DfyOrderBrandingSummary({
  teamExternalId,
  orderExternalId,
  details,
  assetMedias,
}: {
  teamExternalId: string;
  orderExternalId: string;
  details: DfyOrderDetailsDto;
  assetMedias: AssetMediaDto[];
}) {
  const sections = getDfyRequiredSections(details);

  if (sections.length === 0) {
    return null;
  }

  const { propertyDetails, agentDetails } = details;

  const findAssetMedia = (externalId: string | null) =>
    assetMedias.find((assetMedia) => assetMedia.externalId === externalId);

  const handleDownload = async (assetMediaExternalId: string) => {
    try {
      const { downloadUrl } = await DfyApi.getAssetMediaDownloadUrl(
        teamExternalId,
        orderExternalId,
        assetMediaExternalId,
      );
      downloadFile(downloadUrl);
    } catch (error) {
      console.error(error);
      Toast.error("Failed to download image");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="font-medium">Branding</p>
      <div className="flex flex-col gap-4 rounded-xl border p-4 text-sm">
        {sections.includes("propertyDetails") && (
          <BrandingGroup title="Property details">
            <dl className="flex flex-col gap-2">
              <DetailRow label="Address" value={propertyDetails.address} />
              <DetailRow
                label="Highlights"
                value={propertyDetails.highlights}
              />
            </dl>
          </BrandingGroup>
        )}
        {sections.includes("agentDetails") && (
          <BrandingGroup title="Agent details">
            <dl className="flex flex-col gap-2">
              <DetailRow label="Name" value={agentDetails.name} />
              <DetailRow label="Title" value={agentDetails.title} />
              <DetailRow label="Brokerage" value={agentDetails.company} />
              <DetailRow label="Phone" value={agentDetails.phone} />
              <DetailRow label="Email" value={agentDetails.email} />
            </dl>
            <p className="mt-2">Headshot</p>
            <BrandingImage
              assetMedia={findAssetMedia(
                agentDetails.headshotAssetMediaExternalId,
              )}
              fit="cover"
              onDownload={handleDownload}
            />
          </BrandingGroup>
        )}
        {sections.includes("logo") && (
          <BrandingGroup title="Logo">
            <BrandingImage
              assetMedia={findAssetMedia(details.logoAssetMediaExternalId)}
              fit="contain"
              onDownload={handleDownload}
            />
          </BrandingGroup>
        )}
      </div>
    </div>
  );
}
